import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { FlaskConical, ArrowLeft, CheckCircle2, Loader2, MapPin, Wifi, Info, Clock, Sparkles, Lock, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const CONTACT_FIELDS = [
  { key: "full_name", labelKey: "residencyApplyUi.fullNameLabel", type: "text", placeholderKey: "residencyApplyUi.fullNamePlaceholder", required: true },
  { key: "email", labelKey: "residencyApplyUi.emailLabel", type: "email", placeholderKey: "residencyApplyUi.emailPlaceholder", required: true },
  { key: "phone", labelKey: "residencyApplyUi.phoneLabel", type: "tel", placeholderKey: "residencyApplyUi.phonePlaceholder", required: true },
  { key: "country", labelKey: "residencyApplyUi.countryLabel", type: "text", placeholderKey: "residencyApplyUi.countryPlaceholder", required: true },
];

const EXPERIENCE_FIELDS = [
  { key: "experience_years", labelKey: "residencyApplyUi.yearsExperienceLabel", type: "number", placeholderKey: "residencyApplyUi.yearsExperiencePlaceholder", required: true, min: 0, max: 60, half: true },
  { key: "account_size_usd", labelKey: "residencyApplyUi.currentCapitalLabel", type: "number", placeholderKey: "residencyApplyUi.currentCapitalPlaceholder", min: 0, half: true },
  { key: "markets_traded", labelKey: "residencyApplyUi.marketsLabel", type: "text", placeholderKey: "residencyApplyUi.marketsPlaceholder", required: true },
  { key: "referral_source", labelKey: "residencyApplyUi.referralLabel", type: "text", placeholderKey: "residencyApplyUi.referralPlaceholder" },
];

const STAGE_CHOICES = [
  { value: "brand_new", labelKey: "residencyApplyUi.stageBrandNewLabel", bodyKey: "residencyApplyUi.stageBrandNewBody" },
  { value: "dabbling", labelKey: "residencyApplyUi.stageDabblingLabel", bodyKey: "residencyApplyUi.stageDabblingBody" },
  { value: "losing_money", labelKey: "residencyApplyUi.stageLosingLabel", bodyKey: "residencyApplyUi.stageLosingBody" },
  { value: "breakeven", labelKey: "residencyApplyUi.stageBreakevenLabel", bodyKey: "residencyApplyUi.stageBreakevenBody" },
  { value: "consistent_small", labelKey: "residencyApplyUi.stageConsistentLabel", bodyKey: "residencyApplyUi.stageConsistentBody" },
  { value: "profitable_scaling", labelKey: "residencyApplyUi.stageProfitableLabel", bodyKey: "residencyApplyUi.stageProfitableBody" },
];

const BUDGET_CHOICES = [
  { value: "under_1k", labelKey: "residencyApplyUi.budgetUnder1k" },
  { value: "1_5k", labelKey: "residencyApplyUi.budget1to5k" },
  { value: "5_10k", labelKey: "residencyApplyUi.budget5to10k" },
  { value: "10k_plus", labelKey: "residencyApplyUi.budget10kPlus" },
  { value: "flexible", labelKey: "residencyApplyUi.budgetFlexible" },
];

const PROGRAM_META = {
  "residency-bench-side": { labelKey: "residencyApplyUi.programBenchSide", Icon: MapPin, tone: "text-amber-600" },
  "residency-remote-lab": { labelKey: "residencyApplyUi.programRemoteLab", Icon: Wifi, tone: "text-blue-700" },
  "residency-solo-bench": { labelKey: "residencyApplyUi.programSoloBench", Icon: Sparkles, tone: "text-slate-900" },
};

export default function ResidencyApply() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [programId, setProgramId] = useState(
    searchParams.get("program") || "residency-bench-side"
  );
  const [values, setValues] = useState({ commitment_level: 7 });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    if (user) {
      setValues((v) => ({
        ...v,
        full_name: v.full_name ?? user.name ?? "",
        email: v.email ?? user.email ?? "",
      }));
    }
  }, [user]);

  // Members-only gate. `user === false` means the auth check has completed
  // and there is no signed-in user — push them to pricing with a highlight.
  // While the auth state is still loading (`undefined` / `null`), do nothing
  // so we don't redirect a valid session mid-hydration.
  useEffect(() => {
    if (user === false) {
      navigate("/pricing?highlight=residency", { replace: true });
    }
  }, [user, navigate]);
  if (user === false) return null;
  if (user && typeof user === "object" && user.role !== "admin" && user.tier !== "premium") {
    return (
      <div data-testid="residency-apply-locked" className="max-w-xl mx-auto px-6 py-24 text-center">
        <Lock className="mx-auto text-slate-400" size={28} />
        <h1 className="mt-4 font-display font-black text-3xl text-slate-900">{t("residencyApplyUi.premiumOnlyTitle")}</h1>
        <p className="mt-2 text-slate-600">{t("residencyApplyUi.premiumOnlyBody")}</p>
      </div>
    );
  }

  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const isSolo = programId === "residency-solo-bench";
  const soloBlockedReason = (() => {
    if (!isSolo) return null;
    if (!user || typeof user !== "object") {
      return {
        title: t("residencyApplyUi.soloSignInTitle"),
        body: t("residencyApplyUi.soloSignInBody"),
        cta: { to: "/login?redirect=/apply-residency%3Fprogram%3Dresidency-solo-bench", label: t("residencyApplyUi.soloSignInCta") },
      };
    }
    if (user.role !== "admin" && user.tier !== "premium") {
      return {
        title: t("residencyApplyUi.soloPremiumTitle"),
        body: t("residencyApplyUi.soloPremiumBody"),
        cta: { to: "/pricing", label: t("residencyApplyUi.soloPremiumCta") },
      };
    }
    return null;
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side Solo Bench guard — server enforces the same rules, but we
    // catch the obvious cases here to avoid a wasted round-trip.
    if (isSolo) {
      if (soloBlockedReason) {
        toast.error(soloBlockedReason.title);
        return;
      }
      const cap = Number(values.account_size_usd || 0);
      if (!cap || cap < 10000) {
        toast.error(t("residencyApplyUi.soloCapitalError"));
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = { ...values, program_id: programId };
      if (payload.experience_years !== undefined && payload.experience_years !== "") {
        payload.experience_years = Number(payload.experience_years);
      }
      if (payload.account_size_usd === "" || payload.account_size_usd === undefined) {
        delete payload.account_size_usd;
      } else {
        payload.account_size_usd = Number(payload.account_size_usd);
      }
      if (payload.commitment_level !== undefined) {
        payload.commitment_level = Number(payload.commitment_level);
      }
      const { data } = await api.post("/applications/residency", payload);
      setSubmitted(data);
      toast.success(t("residencyApplyUi.applicationReceivedToast"));
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(
        Array.isArray(msg) ? t("residencyApplyUi.validationError") :
        msg || t("residencyApplyUi.submitError")
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <div className="inline-flex w-16 h-16 rounded-full bg-emerald-100 items-center justify-center text-emerald-700 mb-6">
          <CheckCircle2 size={32} />
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl text-slate-900 tracking-tight">
          {t("residencyApplyUi.successTitle")}
        </h1>
        <p className="mt-4 text-slate-600 max-w-lg mx-auto">{submitted.message}</p>
        <p className="mt-2 text-xs text-slate-500">{t("residencyApplyUi.successReference", { id: submitted.application_id })}</p>
        <div className="mt-8 flex justify-center gap-3 flex-wrap">
          <Link to={user ? "/shop" : "/"}>
            <Button className="bg-blue-900 hover:bg-blue-800 text-white h-11 px-6">
              {user ? t("residencyApplyUi.backToShop") : t("residencyApplyUi.backToHome")}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="residency-apply" className="bg-slate-50 min-h-[calc(100vh-4rem)]">
      <div className="max-w-3xl mx-auto px-6 sm:px-10 py-16">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-blue-900 font-semibold mb-8"
        >
          <ArrowLeft size={14} /> {t("residencyApplyUi.back")}
        </button>

        {/* Institutional intro / honest-tone header */}
        <div
          data-testid="residency-apply-intro"
          className="bg-slate-950 text-white rounded-2xl p-8 sm:p-10 mb-6 relative overflow-hidden"
        >
          <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-amber-500/10 blur-3xl" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-amber-400 font-semibold mb-4">
              <FlaskConical size={14} /> {t("residencyApplyUi.introEyebrow")}
            </div>
            <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight leading-tight">
              {t("residencyApplyUi.introTitle")}
            </h1>
            <p className="mt-4 text-slate-300 leading-relaxed max-w-xl">
              {t("residencyApplyUi.introBody")}
            </p>
            <div className="mt-5 flex flex-wrap gap-4 text-xs text-slate-400">
              <span className="inline-flex items-center gap-1.5"><Clock size={12} /> {t("residencyApplyUi.introTime")}</span>
              <span className="inline-flex items-center gap-1.5"><Info size={12} /> {t("residencyApplyUi.introPayment")}</span>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-8 sm:p-10">
          {/* Format toggle */}
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-600 font-semibold">
              {t("residencyApplyUi.formatLabel")}
            </label>
            <div className="mt-2 grid grid-cols-3 gap-2 bg-slate-100 rounded-lg p-1">
              {Object.entries(PROGRAM_META).map(([id, meta]) => {
                const active = programId === id;
                const Icon = meta.Icon;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setProgramId(id)}
                    data-testid={`residency-format-${id}`}
                    className={`inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 rounded-md text-xs sm:text-sm font-semibold transition-all ${
                      active
                        ? "bg-white text-slate-900 shadow-sm"
                        : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    <Icon size={14} className={active ? meta.tone : ""} />
                    <span className="truncate">{t(meta.labelKey)}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Solo Bench prerequisites — surfaced prominently on format switch
              so the applicant sees the bar before they fill anything out. */}
          {isSolo && (
            <div
              data-testid="residency-solo-prereqs"
              className={`mt-6 rounded-2xl p-5 sm:p-6 border ${
                soloBlockedReason
                  ? "bg-red-50 border-red-200"
                  : "bg-amber-50 border-amber-200"
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                  soloBlockedReason
                    ? "bg-red-500/15 border border-red-500/40 text-red-700"
                    : "bg-amber-500/15 border border-amber-500/40 text-amber-700"
                }`}>
                  {soloBlockedReason ? <Lock size={18} /> : <Sparkles size={18} />}
                </div>
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-slate-900">
                    {t("residencyApplyUi.soloPrereqsEyebrow")}
                  </div>
                  <h3 className="font-display font-bold text-lg text-slate-900 mt-1">
                    {soloBlockedReason ? soloBlockedReason.title : t("residencyApplyUi.soloPrereqsTitle")}
                  </h3>
                  <p className="mt-2 text-sm text-slate-700 leading-relaxed">
                    {soloBlockedReason
                      ? soloBlockedReason.body
                      : t("residencyApplyUi.soloPrereqsBody")}
                  </p>
                  {!soloBlockedReason && (
                    <ul className="mt-3 space-y-2 text-sm text-slate-800">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 size={16} className="text-emerald-600 shrink-0 mt-0.5" />
                        <span>
                          {t("residencyApplyUi.soloPrereq1")}
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <DollarSign size={16} className="text-amber-700 shrink-0 mt-0.5" />
                        <span>
                          {t("residencyApplyUi.soloPrereq2")}
                        </span>
                      </li>
                    </ul>
                  )}
                  {soloBlockedReason && soloBlockedReason.cta && (
                    <Link
                      to={soloBlockedReason.cta.to}
                      className="mt-4 inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-4 py-2.5 rounded-md"
                    >
                      {soloBlockedReason.cta.label}
                    </Link>
                  )}
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-8 space-y-10">
            {/* Section 1 — Contact */}
            <Section title={t("residencyApplyUi.sectionWhoYouAre")}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {CONTACT_FIELDS.map((f) => (
                  <FormField key={f.key} field={f} values={values} set={set} />
                ))}
              </div>
            </Section>

            {/* Section 2 — Where you're at (consultative) */}
            <Section title={t("residencyApplyUi.sectionWhereYouAre")}>
              <div>
                <FieldLabel>{t("residencyApplyUi.currentStageLabel")}<Star /></FieldLabel>
                <div
                  data-testid="residency-field-current-stage"
                  className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2"
                >
                  {STAGE_CHOICES.map((c) => {
                    const active = values.current_stage === c.value;
                    return (
                      <label
                        key={c.value}
                        data-testid={`residency-stage-${c.value}`}
                        className={`cursor-pointer rounded-xl border-2 p-4 transition-colors ${
                          active
                            ? "border-blue-800 bg-blue-50"
                            : "border-slate-200 hover:border-slate-300 bg-white"
                        }`}
                      >
                        <input
                          type="radio"
                          name="current_stage"
                          value={c.value}
                          checked={active}
                          onChange={() => set("current_stage", c.value)}
                          className="sr-only"
                          required
                        />
                        <div className="flex items-start gap-2">
                          <div className={`w-4 h-4 rounded-full border-2 mt-0.5 shrink-0 flex items-center justify-center ${
                            active ? "border-blue-800 bg-blue-800" : "border-slate-300"
                          }`}>
                            {active && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                          </div>
                          <div>
                            <div className={`font-display font-semibold text-sm ${active ? "text-blue-900" : "text-slate-900"}`}>
                              {t(c.labelKey)}
                            </div>
                            <div className="mt-0.5 text-xs text-slate-500 leading-relaxed">{t(c.bodyKey)}</div>
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-5">
                <div>
                  <FieldLabel>{t("residencyApplyUi.biggestStruggleLabel")}<Star /></FieldLabel>
                  <Textarea
                    value={values.biggest_struggle ?? ""}
                    onChange={(e) => set("biggest_struggle", e.target.value)}
                    placeholder={t("residencyApplyUi.biggestStrugglePlaceholder")}
                    rows={3}
                    required
                    data-testid="residency-field-biggest_struggle"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <FieldLabel>{t("residencyApplyUi.whyNowLabel")}<Star /></FieldLabel>
                  <Textarea
                    value={values.why_now ?? ""}
                    onChange={(e) => set("why_now", e.target.value)}
                    placeholder={t("residencyApplyUi.whyNowPlaceholder")}
                    rows={3}
                    required
                    data-testid="residency-field-why_now"
                    className="mt-1.5"
                  />
                </div>
              </div>
            </Section>

            {/* Section 3 — Fit signals */}
            <Section title={t("residencyApplyUi.sectionFitSignals")}>
              <div>
                <FieldLabel>{t("residencyApplyUi.educationBudgetLabel")}<Star /></FieldLabel>
                <div
                  data-testid="residency-field-education-budget"
                  className="mt-3 flex flex-wrap gap-2"
                >
                  {BUDGET_CHOICES.map((c) => {
                    const active = values.education_budget === c.value;
                    return (
                      <button
                        key={c.value}
                        type="button"
                        onClick={() => set("education_budget", c.value)}
                        data-testid={`residency-budget-${c.value}`}
                        className={`px-4 h-10 rounded-full border-2 text-sm font-semibold transition-colors ${
                          active
                            ? "border-blue-800 bg-blue-800 text-white"
                            : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                        }`}
                      >
                        {t(c.labelKey)}
                      </button>
                    );
                  })}
                </div>
                {/* Hidden required proxy so browser validation fires */}
                <input type="text" required value={values.education_budget ?? ""} onChange={() => {}} className="sr-only" />
              </div>

              <div>
                <FieldLabel>
                  {t("residencyApplyUi.commitmentLabel")}
                  <span className="ml-2 text-slate-500 font-normal">{t("residencyApplyUi.commitmentScale")}</span>
                </FieldLabel>
                <div className="mt-3 flex items-center gap-4">
                  <input
                    type="range"
                    min="1"
                    max="10"
                    step="1"
                    value={values.commitment_level ?? 7}
                    onChange={(e) => set("commitment_level", Number(e.target.value))}
                    data-testid="residency-field-commitment_level"
                    className="flex-1 accent-blue-800"
                  />
                  <div className="w-14 text-center">
                    <div className="font-display font-bold text-2xl text-blue-900">
                      {values.commitment_level ?? 7}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("residencyApplyUi.commitmentOutOf")}</div>
                  </div>
                </div>
              </div>
            </Section>

            {/* Section 4 — Trading experience */}
            <Section title={t("residencyApplyUi.sectionTradingNumbers")}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {EXPERIENCE_FIELDS.filter((f) => f.half).map((f) => (
                  <FormField key={f.key} field={f} values={values} set={set} />
                ))}
                {EXPERIENCE_FIELDS.filter((f) => !f.half).map((f) => (
                  <div key={f.key} className="sm:col-span-2">
                    <FormField field={f} values={values} set={set} />
                  </div>
                ))}
              </div>
            </Section>

            {/* Section 5 — Goals */}
            <Section title={t("residencyApplyUi.sectionGoals")}>
              <div>
                <FieldLabel>{t("residencyApplyUi.goalsLabel")}<Star /></FieldLabel>
                <Textarea
                  value={values.goals ?? ""}
                  onChange={(e) => set("goals", e.target.value)}
                  placeholder={t("residencyApplyUi.goalsPlaceholder")}
                  rows={5}
                  required
                  data-testid="residency-field-goals"
                  className="mt-1.5"
                />
              </div>
            </Section>

            <div className="pt-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-4">
              <p className="text-[11px] text-slate-500 max-w-md">
                {t("residencyApplyUi.submitDisclaimer")}
              </p>
              <Button
                type="submit"
                disabled={submitting || !!soloBlockedReason}
                data-testid="residency-submit"
                className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold h-12 px-8 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <><Loader2 size={16} className="mr-2 animate-spin" /> {t("residencyApplyUi.submitting")}</>
                ) : (
                  <>{t("residencyApplyUi.submitButton")}</>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="space-y-5">
      <div className="text-xs uppercase tracking-[0.28em] text-blue-800 font-semibold border-l-2 border-amber-500 pl-3">
        {title}
      </div>
      {children}
    </section>
  );
}

function FieldLabel({ children }) {
  return (
    <label className="text-xs uppercase tracking-widest text-slate-600 font-semibold">
      {children}
    </label>
  );
}

function Star() {
  return <span className="text-amber-600 ml-0.5">*</span>;
}

function FormField({ field: f, values, set }) {
  const { t } = useTranslation();
  return (
    <div>
      <FieldLabel>
        {t(f.labelKey)}{f.required && <Star />}
      </FieldLabel>
      <Input
        type={f.type}
        value={values[f.key] ?? ""}
        onChange={(e) => set(f.key, e.target.value)}
        placeholder={t(f.placeholderKey)}
        required={f.required}
        min={f.min}
        max={f.max}
        data-testid={`residency-field-${f.key}`}
        className="mt-1.5 h-11"
      />
    </div>
  );
}
